import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import ContentService from '../services/content';
import contentActions from '../models/content/actions';
import { usePatronSelector } from './use-patron-selector';
import { contentSelectors } from '../models/content';
import {
  trackContentCardLike,
  trackContentCardLikeUpdate,
  trackContentCardUnlike,
} from '../models/content/analytics';
import { useMemo } from 'react';
import { useSWRConfig } from 'swr';
import { getContentLikesDataKey } from './use-content-likes';

type useExpandedReactionsProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
};

export const useContentExpandedReactions = ({
  contentId,
  analyticsData,
}: useExpandedReactionsProps) => {
  const dispatch = useDispatch();
  const { mutate } = useSWRConfig();
  const reactions = usePatronSelector((state) =>
    contentSelectors.getContentReactions(state, { contentId })
  );
  const selectedReaction = useMemo(
    () => reactions?.find((reaction) => reaction.reacted),
    [reactions]
  );
  const availableReactions = useMemo(
    () =>
      reactions?.map((reaction) => ({
        id: reaction.id,
        label: reaction.title,
        emoji: reaction.emoji,
        default: reaction.default,
      })),
    [reactions]
  );

  const { mutate: setNewReaction, isError } = useMutation({
    mutationFn: (reactionId: string | null) => {
      if (!reactionId) {
        return new ContentService(contentId).unlike();
      }
      return new ContentService(contentId).like(reactionId);
    },
    //optimistic update of client state
    onMutate: (reactionId) => {
      dispatch(
        contentActions.updateContent(contentId, {
          expanded_reactions: reactions.map((reaction) => {
            const newReactionData =
              reaction.id === reactionId
                ? { ...reaction, reacted: true, count: reaction.count + 1 }
                : {
                    ...reaction,
                    reacted: false,
                    count: reaction.reacted
                      ? reaction.count - 1
                      : reaction.count,
                  };
            return newReactionData;
          }),
        })
      );
      return [...reactions]; //return snapshot of old data as context
    },
    onSuccess: (data, reactionId) => {
      if (!reactionId) {
        trackContentCardUnlike(contentId, {
          ...analyticsData,
          expanded_reaction_id: reactionId,
        });
      } else {
        if (!selectedReaction) {
          trackContentCardLike(contentId, {
            ...analyticsData,
            expanded_reaction_id: reactionId,
          });
        } else {
          trackContentCardLikeUpdate(contentId, {
            ...analyticsData,
            expanded_reaction_id: reactionId,
          });
        }
      }
    },
    onError: (error, reactionId, context) => {
      console.error('Error in reaction mutation:', error);
      dispatch(
        contentActions.updateContent(contentId, {
          expanded_reactions: context,
        })
      );
    },
    onSettled: (data, error, reactionId) => {
      // Trigger revalidation on the content likes list
      mutate(getContentLikesDataKey(contentId));
      reactionId && mutate(getContentLikesDataKey(contentId, reactionId));
    },
  });

  const handleSetNewReaction = (reactionId: string | null) =>
    reactionId !== selectedReaction?.id && setNewReaction(reactionId);

  return {
    selectedReaction,
    availableReactions,
    setNewReaction: handleSetNewReaction,
    isError,
  };
};
